import { useUI } from '@/store/ui'

export class UnauthorizedError extends Error {
  constructor() {
    super('401 Unauthorized')
  }
}

/** A non-2xx response. Callers branch on `status` — 503 and 502 mean different things to a user. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** The backend's `detail` — a human message (e.g. a 402 upgrade prompt). */
    public readonly detail?: string,
  ) {
    super(message)
  }
}

/**
 * Thin fetch wrapper for the Diwa API. Cookie-based auth (credentials: 'include');
 * a 401 throws UnauthorizedError, which main.tsx catches to redirect to Gate login.
 */
export class DiwaAPI {
  constructor(private baseUrl: string) {}

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = useUI.getState().token
    const headers: Record<string, string> = {}
    if (body) headers['Content-Type'] = 'application/json'
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })
    if (res.status === 401) throw new UnauthorizedError()
    if (!res.ok) {
      const detail = await res.json().then((b) => (b?.detail as string) ?? undefined).catch(() => undefined)
      throw new ApiError(res.status, detail ?? `${method} ${path} failed: ${res.status}`, detail)
    }
    return res.json()
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path)
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PUT', path, body)
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }

  async health(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/health`, {
        credentials: 'include',
        signal: AbortSignal.timeout(3000),
      })
      return res.ok
    } catch {
      return false
    }
  }
}
